import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { EDOS_API_BASE_URL } from '../constants/api';

// Public website + lab-portal RTK Query slice for the eDOS Laravel API.
// All endpoints below mirror /routes/api.php on the eDOS backend
// (TestSpecificationController + LabPackageController). Existing public
// pages (Tests.js, Packages.js, TestDetail.js, PackageDetail.js) already
// use a subset; the lab-portal eDOS screens add directory-style queries
// (departments / specialties / diseases / gender / vitamins / paginated
// list) so the lab portal can render its own catalogue without invoking
// the public layout.
export const edosApi = createApi({
  reducerPath: 'edosApi',
  baseQuery: fetchBaseQuery({ baseUrl: EDOS_API_BASE_URL }),
  // Cache responses for 5 minutes after the last subscriber unmounts.
  // Pages already visited render instantly from cache (the "no delay" UX).
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    // ───────── Public catalogue (existing, unchanged) ─────────

    // Default catalogue (paginated)
    getProfiles: builder.query({
      query: ({ page = 1 } = {}) => `/get-edos/profiles?page=${page}`,
      // Same shape always: { data: [...], pagination: {...} }
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
      }),
    }),

    // Packages list (TestSpecification rows where location = Begumpet)
    getPackages: builder.query({
      query: ({ page = 1, walkIn, homeCollection } = {}) => {
        const params = new URLSearchParams({ page });
        if (walkIn) params.set('walk_in', '1');
        if (homeCollection) params.set('home_collection', '1');
        return `/get-edos/packages?${params.toString()}`;
      },
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
      }),
      transformErrorResponse: (response) => {
        if (response?.status === 404) {
          return { items: [], pagination: null, empty: true };
        }
        return response;
      },
    }),

    // Single test/package by ID — used by detail pages
    getItemById: builder.query({
      query: (id) => `/get-edos/item/${id}`,
      transformResponse: (raw) => raw?.data ?? null,
      transformErrorResponse: (response) => {
        if (response?.status === 404) return { notFound: true };
        return response;
      },
    }),

    // Search across multiple fields (test_name, test_code, departments,
    // diseases, doctor_specialty, parameters, location). Used both by the
    // public Tests page and by the lab portal's eDOS search bar.
    searchTests: builder.query({
      query: ({ typewords, page = 1, perPage = 10 }) =>
        `/get-edos/search?typewords=${encodeURIComponent(typewords)}&page=${page}&per_page=${perPage}`,
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
        searchTerm: raw?.search_term ?? '',
      }),
      // Treat 404 "no results" as empty list, not as an error
      transformErrorResponse: (response) => {
        if (response?.status === 404) {
          return { items: [], pagination: null, searchTerm: '', empty: true };
        }
        return response;
      },
    }),

    // ───────── Lab-portal eDOS catalogue (new) ─────────

    // Paginated tests list — the eDOS catalogue's primary feed for the
    // lab portal's "Tests / Departments / Speciality" pages. Backed by
    // a separate `labPortalTestsList` endpoint (NOT the original
    // `testlistApi`) which accepts ?department, ?speciality, ?disease,
    // ?location, ?keyword, ?gender filters server-side and returns an
    // empty data array (instead of 404) when no rows match.
    //
    // Kept apart from `/get-edos/testsList` deliberately so the
    // original endpoint's `WHERE location='Hyderabad'` legacy default
    // is preserved for whatever consumer still depends on it.
    getTestsList: builder.query({
      query: ({
        page = 1,
        perPage = 10,
        department,
        speciality,
        disease,
        location,
        keyword,
        gender,
        // ?all=1 → fetch every matching row in one shot, no pagination.
        // Used by the in-results search box on Departments/Speciality
        // pages so the partner sees every match across all pages.
        all = false,
      } = {}) => {
        const params = new URLSearchParams();
        if (all) {
          params.set('all', '1');
        } else {
          params.set('page', page);
          params.set('per_page', perPage);
        }
        if (department) params.set('department', department);
        if (speciality) params.set('speciality', speciality);
        if (disease)    params.set('disease', disease);
        if (location)   params.set('location', location);
        if (keyword)    params.set('keyword', keyword);
        if (gender)     params.set('gender', gender);
        return `/get-edos/lab/tests?${params.toString()}`;
      },
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
      }),
      transformErrorResponse: (response) => {
        if (response?.status === 404) {
          return { items: [], pagination: null, empty: true };
        }
        return response;
      },
    }),

    // All tests at all locations — admin/sales-only fat fetch. The lab
    // portal's eDOS pages use this for client-side dept/speciality
    // grouping until the backend adds proper aggregate endpoints.
    getTestsListAll: builder.query({
      query: () => `/get-edos/testsList/sales/all/locations`,
      transformResponse: (raw) => ({ items: raw?.data ?? [] }),
      transformErrorResponse: (response) => {
        if (response?.status === 404) return { items: [], empty: true };
        return response;
      },
    }),

    // Distinct departments (CSV-flattened, deduped server-side)
    getDepartments: builder.query({
      query: () => `/get-edos/departments`,
      // Backend returns a flat array of strings (uniqueDepartments)
      // Backend returns either a JSON array (`["A", "B"]`) or — when
      // PHP's array_unique() preserves the original integer keys —
      // a JSON object (`{"0": "A", "21": "B"}`). Object.values()
      // handles both cleanly.
      transformResponse: (raw) => {
        if (Array.isArray(raw)) return raw;
        if (raw?.data) return Array.isArray(raw.data) ? raw.data : Object.values(raw.data);
        if (raw && typeof raw === 'object') return Object.values(raw);
        return [];
      },
    }),

    // Distinct doctor specialties
    getSpecialties: builder.query({
      query: () => `/get-edos/specialties`,
      // Backend returns either a JSON array (`["A", "B"]`) or — when
      // PHP's array_unique() preserves the original integer keys —
      // a JSON object (`{"0": "A", "21": "B"}`). Object.values()
      // handles both cleanly.
      transformResponse: (raw) => {
        if (Array.isArray(raw)) return raw;
        if (raw?.data) return Array.isArray(raw.data) ? raw.data : Object.values(raw.data);
        if (raw && typeof raw === 'object') return Object.values(raw);
        return [];
      },
    }),

    // Distinct diseases — used by the eDOS "Search by disease" filter
    getDiseases: builder.query({
      query: () => `/get-edos/diseases`,
      // Backend returns either a JSON array (`["A", "B"]`) or — when
      // PHP's array_unique() preserves the original integer keys —
      // a JSON object (`{"0": "A", "21": "B"}`). Object.values()
      // handles both cleanly.
      transformResponse: (raw) => {
        if (Array.isArray(raw)) return raw;
        if (raw?.data) return Array.isArray(raw.data) ? raw.data : Object.values(raw.data);
        if (raw && typeof raw === 'object') return Object.values(raw);
        return [];
      },
    }),

    // Tests filtered by patient gender (M / F / MALE / FEMALE)
    getTestsByGender: builder.query({
      query: ({ gender, page = 1, perPage = 10 }) =>
        `/get-edos/gender/${encodeURIComponent(gender)}?page=${page}&per_page=${perPage}`,
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
      }),
      transformErrorResponse: (response) => {
        if (response?.status === 404) return { items: [], pagination: null, empty: true };
        return response;
      },
    }),

    // Vitamin-specific tests — the controller accepts one of vitaminD,
    // vitaminB12, vitaminB, vitaminC, vitaminE, vitaminK, vitaminA as a
    // query param. We pass the chosen key as `which` and translate.
    getVitaminTests: builder.query({
      query: ({ which = 'vitaminD', page = 1, perPage = 10 }) =>
        `/get-edos/vitamins?${which}=1&page=${page}&per_page=${perPage}`,
      transformResponse: (raw) => ({
        items: raw?.data ?? [],
        pagination: raw?.pagination ?? null,
        vitamin: raw?.vitamin_search ?? '',
      }),
      transformErrorResponse: (response) => {
        if (response?.status === 404) return { items: [], pagination: null, empty: true };
        return response;
      },
    }),

    // Lookup test by test_code — backend returns
    //   { success: true, count: N, data: [{...}] }
    // i.e. `data` is always an array even for a single match. Unwrap
    // to the first row so consumers (EdosTestDetail) read a plain
    // object instead of `[{…}].test_name === undefined` and showing
    // all fields as dashes. Falls back to the raw value if the API
    // ever switches to returning a single object.
    getTestByCode: builder.query({
      query: (testCode) =>
        `/get-test-specification-by-code?test_code=${encodeURIComponent(testCode)}`,
      transformResponse: (raw) => {
        const d = raw?.data;
        if (Array.isArray(d)) return d[0] ?? null;
        return d ?? raw ?? null;
      },
      transformErrorResponse: (response) => {
        if (response?.status === 404) return { notFound: true };
        return response;
      },
    }),
  }),
});

export const {
  // Public catalogue
  useGetProfilesQuery,
  useGetPackagesQuery,
  useGetItemByIdQuery,
  useSearchTestsQuery,
  // Lab-portal eDOS
  useGetTestsListQuery,
  useGetTestsListAllQuery,
  useGetDepartmentsQuery,
  useGetSpecialtiesQuery,
  useGetDiseasesQuery,
  useGetTestsByGenderQuery,
  useGetVitaminTestsQuery,
  useGetTestByCodeQuery,
  usePrefetch,
} = edosApi;
